import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Shield, Briefcase, Code, GraduationCap, TrendingUp, User, Megaphone, ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import { Footer, DebateSandbox } from '../components/marketing';

const SEGMENTS = [
  {
    id: 'startup',
    title: 'Startup Founders',
    tagline: 'The board of advisors you can\'t afford',
    description: 'Stress-test pivots, validate GTM strategy, and get five expert perspectives on your toughest decisions — without the $50K advisory retainer.',
    icon: Zap,
    color: '#FF4D00',
    prompt: 'Should we pivot our B2B SaaS to target enterprise clients, or double down on SMB self-serve?',
  },
  {
    id: 'enterprise',
    title: 'Enterprise Teams',
    tagline: 'Auditable AI governance for every decision',
    description: 'Run AI-powered decision committees with full audit trails, RBAC, and compliance documentation that satisfies procurement.',
    icon: Shield,
    color: '#00D4FF',
    prompt: 'Should we migrate our customer data infrastructure to a multi-cloud architecture?',
  },
  {
    id: 'consultant',
    title: 'Consultants & Agencies',
    tagline: 'Better client recommendations, faster',
    description: 'Deliver multi-perspective analysis for client engagements. Generate recommendation memos that show you considered every angle.',
    icon: Briefcase,
    color: '#F2A900',
    prompt: 'Our client is choosing between Salesforce and HubSpot — what should we recommend?',
  },
  {
    id: 'engineering',
    title: 'Product & Engineering',
    tagline: 'Architecture reviews before you commit',
    description: 'Evaluate technical decisions with AI voices playing architect, security lead, DevOps, and product manager. Catch blind spots before they ship.',
    icon: Code,
    color: '#FF4D00',
    prompt: 'Monolith vs microservices for our new payment processing system?',
  },
  {
    id: 'academic',
    title: 'Academics & Researchers',
    tagline: 'Thesis defense simulation',
    description: 'Simulate peer review, test methodology rigor, and prepare for defense with AI voices that challenge your assumptions from multiple disciplines.',
    icon: GraduationCap,
    color: '#7C3AED',
    prompt: 'Is my methodology sound? Evaluate this mixed-methods research design for studying remote work productivity.',
  },
  {
    id: 'finance',
    title: 'Investment & Finance',
    tagline: 'Investment committee without groupthink',
    description: 'Run investment committee debates with voices playing bull case, bear case, risk analyst, and portfolio manager. Make better allocation decisions.',
    icon: TrendingUp,
    color: '#22C55E',
    prompt: 'Should we invest in this Series A AI startup at a $40M valuation given current market conditions?',
  },
  {
    id: 'personal',
    title: 'Personal Decisions',
    tagline: 'Life decisions, multiple perspectives',
    description: 'Career moves, relocations, major purchases — get the rigorous multi-perspective analysis that friends and family can\'t provide objectively.',
    icon: User,
    color: '#00D4FF',
    prompt: 'I have two job offers — one at a startup with equity, one at BigCo with 40% more salary. Help me decide.',
  },
  {
    id: 'marketing',
    title: 'Marketing & Growth',
    tagline: 'Campaign strategy debated before spend',
    description: 'Before committing budget, run your strategy through voices playing CMO, data analyst, brand strategist, and CFO. Find the blind spots.',
    icon: Megaphone,
    color: '#F2A900',
    prompt: 'Should we allocate 60% of Q3 budget to paid social or shift toward content marketing and SEO?',
  },
];

export default function SolutionsPage() {
  const [activeSegment, setActiveSegment] = useState(null);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 md:pt-36 md:pb-24 md:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-display-lg md:text-display-xl font-display text-[#F5F1E8] uppercase mb-6">
              Solutions for Every<br />
              <span className="text-[#FF4D00]">Decision Maker</span>
            </h1>
            <p className="text-lg text-[rgba(245,241,232,0.72)] max-w-2xl mx-auto mb-4 font-body leading-relaxed">
              Whether you're evaluating a startup pivot, defending a thesis, or allocating a $10M budget —
              Fray assembles the right committee for your decision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Segment Grid */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SEGMENTS.map((seg, i) => (
              <motion.button
                key={seg.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                onClick={() => setActiveSegment(activeSegment === seg.id ? null : seg.id)}
                className={`group text-left p-5 border transition-all ${
                  activeSegment === seg.id
                    ? 'bg-[#1E1E1E] border-[rgba(245,241,232,0.16)]'
                    : 'bg-[#1E1E1E]/50 border-[rgba(245,241,232,0.08)] hover:border-[rgba(245,241,232,0.16)]'
                }`}
              >
                <div className="h-[2px] w-full mb-4 -mt-5 -mx-5" style={{ width: 'calc(100% + 2.5rem)', backgroundColor: activeSegment === seg.id ? seg.color : 'transparent' }} />
                <seg.icon className="w-5 h-5 mb-3 transition-colors" style={{ color: seg.color }} />
                <h3 className="text-sm font-display font-bold text-[#F5F1E8] uppercase tracking-tight mb-1">
                  {seg.title}
                </h3>
                <p className="text-xs font-mono tracking-wider mb-3" style={{ color: seg.color }}>
                  {seg.tagline}
                </p>
                <p className="text-xs text-[rgba(245,241,232,0.56)] leading-relaxed font-body">
                  {seg.description}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Segment Sandbox */}
      {activeSegment && (
        <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
          <div className="max-w-5xl mx-auto">
            <motion.div
              key={activeSegment}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h2 className="text-display-sm font-display text-[#F5F1E8] uppercase mb-2">
                Try It: {SEGMENTS.find(s => s.id === activeSegment)?.title}
              </h2>
              <p className="text-sm text-[rgba(245,241,232,0.72)] mb-6 font-body">
                Run a sample debate for this use case. See how five AI voices analyze your decision.
              </p>
              <DebateSandbox segment={activeSegment} />
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 md:px-6 border-t border-[rgba(245,241,232,0.08)]">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-display-md font-display text-[#F5F1E8] uppercase mb-4">
              Don't see your use case?
            </h2>
            <p className="text-sm text-[rgba(245,241,232,0.72)] mb-8 font-body">
              Fray works for any decision that benefits from multiple perspectives. Build a custom committee.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4D00] text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:bg-[#FF6B33] transition-colors"
            >
              Start Debating Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
